/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, NgZone, OnInit} from '@angular/core';
import {FormsModule, NgForm} from '@angular/forms';
import {finalize, Subject} from 'rxjs';
import * as _ from 'lodash';
import {BsModalRef} from 'ngx-bootstrap/modal';
import template from './segment-trigger-modal.component.html';
import {TriggerFormLogic} from '../trigger/trigger-form-logic';
import {Uuid} from '../../components/util/uuid';
import {AppEventBusService} from '../common/events/app-event-bus.service';
import {
  clearFormValidation,
  createFormSubmitState,
  exposeFormSubmitStateAccessors,
  FormControlError,
  FormSubmitState,
  handleApiValidationErrors,
  markFormSubmitError,
  markFormSubmitSuccess,
  updateFormWarnings
} from '../common/controller/form-submit-state';
import {FolderApiService} from '../folder/folder-api.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocationApiService} from '../trigger/location-api.service';
import {VisitorProfileFieldsApiService} from '../fields/visitor-profile-fields-api.service';
import {SearchApiService} from '../common/controller/search-api.service';
import {TriggerApiService, TriggerModel} from '../trigger/trigger-api.service';
import {APP_SHARED} from '../common/app-shared';
import {TriggerFormComponent} from '../trigger/trigger-form.component';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, TriggerFormComponent],
  selector: 'adn-segment-trigger-modal',
  template
})
export class SegmentTriggerModalComponent implements OnInit {
  @Input() segmentModel: any;
  @Input() entry: any;

  readonly onClosed = new Subject<void>();

  apiModel: any;
  editingModel: any = {};
  formLogic: TriggerFormLogic;
  submitState: FormSubmitState = createFormSubmitState();
  pending = false;

  private readonly formControlErrors: FormControlError[] = [];

  constructor(
    public bsModalRef: BsModalRef,
    private readonly eventBus: AppEventBusService,
    private readonly triggerApi: TriggerApiService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly folderApi: FolderApiService,
    private readonly searchApi: SearchApiService,
    private readonly locationApi: LocationApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly visitorProfileFieldsApi: VisitorProfileFieldsApiService,
    private readonly ngZone: NgZone
  ) {
    exposeFormSubmitStateAccessors(this, this.submitState);
  }

  ngOnInit(): void {
    this.apiModel = this.entry || {
      addUserSegments: [{id: this.segmentModel.id, name: this.segmentModel.name}]
    };
    this.editingModel = _.cloneDeep(this.apiModel);
    this.editingModel.helpfulCriteria = this.editingModel.helpfulCriteria || [];

    this.formLogic = new TriggerFormLogic(this.editingModel, {
      adnTimeShifter: this.adnTimeShifter,
      folderApi: this.folderApi,
      searchApi: this.searchApi,
      visitorProfileFieldsApi: this.visitorProfileFieldsApi,
      locationApi: this.locationApi
    });
  }

  markDirty(editForm: NgForm): void {
    editForm.control.markAsDirty();
  }

  submit(editForm: NgForm): void {
    if (this.pending) {
      return;
    }

    this.pending = true;
    this.submitState.ngForm = editForm;
    clearFormValidation(this.submitState, this.formControlErrors);

    this.formLogic.onBeforeSubmit();

    if (!this.editingModel.id) {
      this.editingModel.id = Uuid.generate();
    }

    _.forEach(this.editingModel, (val, key) => {
      if (key.charAt(0) !== '$' && !_.isFunction(val)) {
        this.apiModel[key] = val;
      }
    });

    this.triggerApi.save(this.editingModel as TriggerModel).pipe(
      finalize(() => {
        this.pending = false;
        this.eventBus.emit('SUBMIT_PROCESSED');
      })
    ).subscribe({
      next: (apiResource) => {
        this.ngZone.run(() => {
          updateFormWarnings(this.submitState, apiResource || this.editingModel, this.LocalNetworkProfile.isPureMarketplacePlatform());
          if (apiResource) {
            _.merge(this.editingModel, apiResource);
            Object.assign(this.apiModel, apiResource);
          }
          markFormSubmitSuccess(this.submitState, editForm);
          this.bsModalRef.hide();
          this.onClosed.next();
        });
      },
      error: (response: any) => {
        if ((response.status === 400 || response.status === 403 || response.status === 422)
          && (response.data.type === 'ValidationResult' || response.data.type === 'ErrorMessage')) {
          handleApiValidationErrors(
            this.submitState,
            this.editingModel,
            _.get(response, ['data', 'errors']) || response.data,
            this.formControlErrors,
            editForm
          );
        }
        markFormSubmitError(this.submitState, editForm);
      }
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
    this.onClosed.next();
  }
}
